import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useVideoTutorial } from '@/context/VideoTutorialContext';
import { fetchAdminMenuStructure, fetchGranularPermissions } from '@/lib/adminAccessCache';
import { Button } from '@/components/ui/button';
import UserDropdown from '@/components/UserDropdown';
import { SecteurSelectorDropdown, userCanSwitchSecteur } from '@/components/SecteurSelector';
import AdminPinModal from '@/components/AdminPinModal';
import { useGroup } from '@/context/GroupContext';
import { isPrysmeAccessApp, openPrysmeAccessApp } from '@/utils/prysmeAccessApp';
import { toast } from 'sonner';
import { 
  Lock, 
  Star, 
  ChevronLeft, 
  ChevronRight,
  Menu,
  X,
  GripVertical,
  BookOpen,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  isAppDisabled,
  getDisabledFavoriteRowClass,
  getDisabledFavoriteIconClass,
  getDisabledFavoriteLetterClass,
} from '@/utils/applicationHomeStatus';
import { useAppSecteurScope } from '@/context/AppSecteurScopeContext';
import axios from 'axios';

// Helper pour gérer les URLs d'images
const getImageUrl = (imageValue, API) => {
  if (!imageValue) return null;
  if (imageValue.startsWith('data:')) return imageValue;
  if (imageValue.startsWith('http')) return imageValue;
  return `${API}/files/${imageValue}`;
};

const isMobileDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  if (/iPhone|iPad|iPod/i.test(userAgent)) return true;
  if (/Android/i.test(userAgent)) return true;
  return false;
};

function favoriteAppLabelClass(disabled, showPendingPulse) {
  if (disabled) return 'text-gray-400 dark:text-gray-500';
  if (showPendingPulse) return 'pending-docs-app-list-name-blink font-semibold';
  return 'text-gray-700 dark:text-gray-300';
}

// Composant favori draggable
const SortableFavoriteItem = ({ app, onOpenApp, onToggleFavorite, isCollapsed, setIsMobileOpen, hasPendingDocuments }) => {
  const scopeCtx = useAppSecteurScope();
  const disabled = isAppDisabled(app, scopeCtx);
  const showPendingPulse = hasPendingDocuments && isPrysmeAccessApp(app);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: app.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={getDisabledFavoriteRowClass(
        disabled,
        `group flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${isCollapsed ? 'justify-center' : ''} ${isDragging ? 'bg-gray-100 dark:bg-gray-700' : ''}`
      )}
      title={isCollapsed ? app.name : undefined}
    >
      {isCollapsed ? (
        /* En mode réduit, afficher la première lettre */
        <div 
          {...attributes}
          {...listeners}
          onClick={() => {
            onOpenApp(app);
            setIsMobileOpen(false);
          }}
          className={getDisabledFavoriteIconClass(disabled)}
        >
          <span className={`${getDisabledFavoriteLetterClass(disabled)} ${showPendingPulse && !disabled ? 'pending-docs-app-list-name-blink' : ''}`}>
            {app.name.charAt(0).toUpperCase()}
          </span>
        </div>
      ) : (
        /* En mode étendu */
        <>
          <div
            {...attributes}
            {...listeners}
            className="flex-1 flex items-center gap-2 cursor-grab active:cursor-grabbing hover:bg-gray-200 dark:hover:bg-gray-600 rounded px-1 py-0.5 min-w-0"
            title="Glissez pour réorganiser"
          >
            <GripVertical size={14} className="text-gray-400 flex-shrink-0" />
            <span 
              className={`flex-1 text-sm truncate ${favoriteAppLabelClass(disabled, showPendingPulse)}`}
              onClick={(e) => {
                e.stopPropagation();
                onOpenApp(app);
                setIsMobileOpen(false);
              }}
            >
              {app.name}
            </span>
          </div>
          {/* Bouton supprimer favori */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(app.id);
            }}
            className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 dark:hover:bg-red-900 rounded transition-all flex-shrink-0"
          >
            <X size={14} className="text-red-500" />
          </button>
        </>
      )}
    </div>
  );
};

/** Contenu sidebar hors de `Sidebar` pour éviter un remount à chaque re-render parent (sinon UserDropdown perd son état ouvert). */
function SidebarInner({
  isCollapsed,
  setIsCollapsed,
  user,
  token,
  updateUser,
  onLogout,
  API,
  favoriteApps,
  onOpenApp,
  onToggleFavorite,
  onReorderFavorites,
  setIsMobileOpen,
  hasAdminAccess,
  isAdminPage,
  handleAdminClick,
  setCurrentSecteur,
}) {
  const { openHelpPanel, getUnviewedCount } = useVideoTutorial();
  const { pendingDocuments, hasBlockingPendingDocuments } = useGroup();
  const hasPendingDocuments = pendingDocuments.length > 0;
  const openAppGuarded = useCallback((app) => {
    if (hasBlockingPendingDocuments && !isPrysmeAccessApp(app)) {
      toast.error("Signez vos documents obligatoires dans Prysm'Access pour continuer.");
      window.dispatchEvent(new CustomEvent('open-prysme-access-documents'));
      return;
    }
    onOpenApp(app);
  }, [hasBlockingPendingDocuments, onOpenApp]);
  const scopeCtx = useAppSecteurScope();
  const isMobile = useMemo(() => isMobileDevice(), []);
  const unviewedCount = getUnviewedCount();
  const [appVersion, setAppVersion] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/app-versions/central/published`)
      .then((res) => {
        if (!cancelled) setAppVersion(res.data?.version || null);
      })
      .catch(() => {
        if (!cancelled) setAppVersion(null);
      });
    return () => {
      cancelled = true;
    };
  }, [API]);

  // Ouvre Prysme Access (documents) depuis le modal post-connexion
  useEffect(() => {
    const handler = () => {
      openPrysmeAccessApp({ API, user }).catch((err) => {
        console.error('Ouverture Prysme Access:', err);
      });
    };
    window.addEventListener('open-prysme-access-documents', handler);
    return () => window.removeEventListener('open-prysme-access-documents', handler);
  }, [API, user]);

  const handleOpenHelp = useCallback(() => {
    openHelpPanel('user');
    setIsMobileOpen(false);
  }, [openHelpPanel, setIsMobileOpen]);

  return (
    <div className="flex flex-col h-full">
      {/* Zone A - Logo/Identité */}
      <div className={`p-4 border-b border-gray-200 dark:border-gray-700 ${isCollapsed ? 'px-2' : ''}`}>
        <div className="flex items-center justify-center gap-2">
          {isCollapsed ? (
            <div className="flex flex-col items-center gap-1">
              <img
                src="/logo-prysma.png"
                alt="Prysm'App"
                className="h-10 w-10 object-contain"
              />
              {appVersion ? (
                <span className="text-[10px] leading-none text-gray-500 dark:text-gray-400">v{appVersion}</span>
              ) : null}
            </div>
          ) : (
            <>
              <img
                src="/logo-prysma.png"
                alt="Prysm'App"
                className="h-10 w-auto object-contain"
              />
              <div className="flex min-w-0 flex-col items-start text-left">
                <h1
                  className="text-xl font-bold text-[#080808] dark:text-white leading-tight"
                  style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800 }}
                >
                  Prysm&apos;App
                </h1>
                {appVersion ? (
                  <p className="text-xs font-normal text-gray-500 dark:text-gray-400 truncate">
                    v{appVersion}
                  </p>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Zone B - Contexte Utilisateur */}
      <div 
        className={`p-3 border-b border-gray-200 dark:border-gray-700 ${isCollapsed ? 'px-2' : ''}`}
        data-testid="sidebar-user-dropdown"
      >
        <UserDropdown
          user={user}
          onLogout={onLogout}
          API={API}
          isCollapsed={isCollapsed}
        />
        {/* Sélecteur de secteur : uniquement si ≥ 2 secteurs autorisés */}
        {!isCollapsed && userCanSwitchSecteur(user) && (
          <div className="mt-3">
            <SecteurSelectorDropdown
              user={user}
              token={token}
              updateUser={updateUser}
              onSecteurChange={(secteur) => setCurrentSecteur(secteur)}
            />
          </div>
        )}
      </div>

      {/* Zone C - Navigation Principale (Favoris) */}
      <div className="flex-1 overflow-y-auto p-3" data-testid="sidebar-favorites">
        {/* Favoris */}
        {favoriteApps.length > 0 && (
          <div className={`${isCollapsed ? '' : ''}`}>
            <div className={`flex items-center gap-2 mb-3 ${isCollapsed ? 'justify-center' : ''}`}>
              <Star size={16} className="text-[#f4af14] fill-[#f4af14]" />
              {!isCollapsed && (
                <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Favoris
                </h3>
              )}
            </div>
            <div className="space-y-1">
              {favoriteApps.map((app, index) => {
                const disabled = isAppDisabled(app, scopeCtx);
                const showPendingPulse = hasPendingDocuments && isPrysmeAccessApp(app);
                return (
                <div
                  key={app.id}
                  className={getDisabledFavoriteRowClass(
                    disabled,
                    `group flex items-center gap-1 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${isCollapsed ? 'justify-center' : ''}`
                  )}
                  title={isCollapsed ? app.name : undefined}
                >
                  {isCollapsed ? (
                    <div 
                      onClick={() => {
                        openAppGuarded(app);
                        setIsMobileOpen(false);
                      }}
                      className={getDisabledFavoriteIconClass(disabled)}
                    >
                      <span className={`${getDisabledFavoriteLetterClass(disabled)} ${showPendingPulse && !disabled ? 'pending-docs-app-list-name-blink' : ''}`}>
                        {app.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  ) : (
                    <>
                      {/* Boutons de réorganisation */}
                      <div className="flex flex-col opacity-0 group-hover:opacity-100 transition-opacity">
                        {index > 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onReorderFavorites) {
                                const newOrder = [...favoriteApps];
                                [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
                                onReorderFavorites(newOrder.map(a => a.id));
                              }
                            }}
                            className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-400 hover:text-gray-600"
                            title="Monter"
                            data-testid={`favorite-move-up-${app.id}`}
                          >
                            <ChevronLeft size={12} className="rotate-90" />
                          </button>
                        )}
                        {index < favoriteApps.length - 1 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onReorderFavorites) {
                                const newOrder = [...favoriteApps];
                                [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
                                onReorderFavorites(newOrder.map(a => a.id));
                              }
                            }}
                            className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-400 hover:text-gray-600"
                            title="Descendre"
                            data-testid={`favorite-move-down-${app.id}`}
                          >
                            <ChevronRight size={12} className="rotate-90" />
                          </button>
                        )}
                      </div>
                      {/* Nom cliquable */}
                      <span 
                        className={`flex-1 text-sm truncate cursor-pointer ${favoriteAppLabelClass(disabled, showPendingPulse)}`}
                        onClick={() => {
                          openAppGuarded(app);
                          setIsMobileOpen(false);
                        }}
                        data-testid={`favorite-open-${app.id}`}
                      >
                        {app.name}
                      </span>
                      {/* Bouton supprimer favori */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleFavorite(app.id);
                        }}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 dark:hover:bg-red-900 rounded transition-all flex-shrink-0"
                        data-testid={`favorite-remove-${app.id}`}
                      >
                        <X size={14} className="text-red-500" />
                      </button>
                    </>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Message si pas de favoris */}
        {favoriteApps.length === 0 && !isCollapsed && (
          <div className="text-center py-8">
            <Star size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Pas encore de favoris
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Cliquez sur ★ pour ajouter
            </p>
          </div>
        )}
      </div>

      {/* Zone D - Pied : Centre d&apos;aide puis Administration */}
      {(!isMobile || hasAdminAccess) && (
        <div className={`p-3 border-t border-gray-200 dark:border-gray-700 ${isCollapsed ? 'px-2' : ''}`}>
          <div className="flex flex-col gap-2">
            {!isMobile && (
              <button
                type="button"
                onClick={handleOpenHelp}
                className={`w-full flex items-center gap-2 rounded-lg text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                  isCollapsed ? 'justify-center p-2' : 'relative justify-center px-3 py-2.5'
                }`}
                data-testid="help-button"
                title={
                  isCollapsed
                    ? unviewedCount > 0
                      ? `Centre d'aide — ${unviewedCount} à voir`
                      : "Centre d'aide"
                    : unviewedCount > 0
                      ? `Centre d'aide — ${unviewedCount} à voir`
                      : undefined
                }
                aria-label={
                  unviewedCount > 0
                    ? `Centre d'aide, ${unviewedCount} élément${unviewedCount > 1 ? 's' : ''} à voir`
                    : undefined
                }
              >
                <span className="relative flex-shrink-0 inline-flex">
                  <BookOpen
                    size={20}
                    className={`text-[#f76f4b] ${
                      unviewedCount > 0 ? 'motion-safe:animate-help-book-attention' : ''
                    }`}
                    aria-hidden
                  />
                  {isCollapsed && unviewedCount > 0 && (
                    <span
                      className="absolute -right-1 -top-0.5 h-2 min-w-[8px] rounded-full bg-purple-500 ring-2 ring-white dark:ring-gray-800"
                      aria-hidden
                    />
                  )}
                </span>
                {!isCollapsed && (
                  <span className="shrink-0 whitespace-nowrap">Centre d&apos;aide</span>
                )}
              </button>
            )}
            {hasAdminAccess && (
              <Button
                onClick={handleAdminClick}
                variant={isAdminPage ? "default" : "outline"}
                data-testid="admin-button"
                className={`w-full ${
                  isAdminPage 
                    ? 'bg-[#f76f4b] hover:bg-[#e55d3b] text-white' 
                    : 'border-[#f76f4b] text-[#f76f4b] hover:bg-[#f76f4b] hover:text-white dark:border-[#f76f4b] dark:text-[#f76f4b]'
                } ${isCollapsed ? 'px-2' : ''}`}
              >
                <Lock size={16} className={isCollapsed ? '' : 'mr-2'} />
                {!isCollapsed && 'Administration'}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Bouton collapse */}
      <div className="p-2 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="w-full p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center justify-center"
          title={isCollapsed ? "Agrandir" : "Réduire"}
        >
          {isCollapsed ? (
            <ChevronRight size={20} className="text-gray-500" />
          ) : (
            <ChevronLeft size={20} className="text-gray-500" />
          )}
        </button>
      </div>
    </div>
  );
}

const Sidebar = ({ 
  favoriteApps = [], 
  onOpenApp, 
  onToggleFavorite,
  onReorderFavorites,
  onLogout,
  API 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, updateUser } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [activeFavoriteId, setActiveFavoriteId] = useState(null);
  const [currentSecteur, setCurrentSecteur] = useState(null);
  const [granularPermissions, setGranularPermissions] = useState(null);
  const [menuStructure, setMenuStructure] = useState([]);

  // Charger les permissions granulaires et la structure des menus
  useEffect(() => {
    const loadData = async () => {
      if (!user?.id || user?.is_admin) return;
      
      try {
        const [permData, structureData] = await Promise.all([
          fetchGranularPermissions(user.id),
          fetchAdminMenuStructure(),
        ]);
        setGranularPermissions(permData);
        setMenuStructure(structureData);
      } catch (error) {
        console.error('Erreur chargement permissions/structure:', error);
      }
    };
    
    loadData();
  }, [user?.id, user?.is_admin, API]);

  // Calculer si l'utilisateur a accès à l'administration
  // Ne compte que les onglets encore présents dans la structure (ex: ancien "Horaires" Entreprises ignoré)
  const hasAdminAccess = (() => {
    if (user?.is_admin) return true;
    if (!granularPermissions?.sections?.length) return false;
    
    return granularPermissions.sections.some(sectionPerm => {
      if (sectionPerm.can_create || sectionPerm.can_delete) return true;
      const section = menuStructure.find(s => s.id === sectionPerm.section_id);
      const validTabIds = new Set((section?.tabs || []).map(t => t.id));
      return sectionPerm.tabs?.some(
        tab => validTabIds.has(tab.tab_id) && (tab.can_read || tab.can_edit)
      );
    });
  })();

  // Sensors pour le drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  // Trouver le favori actif pour le DragOverlay
  const activeFavorite = activeFavoriteId ? favoriteApps.find(app => app.id === activeFavoriteId) : null;

  // Handlers pour le drag des favoris
  const handleFavoriteDragStart = (event) => {
    console.log('Favorite drag start:', event.active.id);
    setActiveFavoriteId(event.active.id);
  };

  const handleFavoriteDragEnd = (event) => {
    console.log('Favorite drag end:', event.active?.id, event.over?.id);
    setActiveFavoriteId(null);
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = favoriteApps.findIndex(app => app.id === active.id);
    const newIndex = favoriteApps.findIndex(app => app.id === over.id);
    console.log('Moving from index', oldIndex, 'to', newIndex);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(favoriteApps, oldIndex, newIndex);
      if (onReorderFavorites) {
        onReorderFavorites(newOrder.map(app => app.id));
      }
    }
  };

  // Mapping des sections vers les routes d'administration
  const sectionToRoute = {
    'users': '/admin/users',
    'companies': '/admin/companies',
    'applications': '/admin/applications',
    'secteurs': '/admin/services',
    'suppliers': '/admin/suppliers',
    'sites': '/admin/entreprises',
    'backup': '/admin/backup/exports',
    'api': '/admin/api/microsoft/accounts',
    'tutorials': '/admin/tutorials',
  };

  // Trouver la première section autorisée (ne compte que les onglets encore dans la structure)
  const getFirstAllowedRoute = () => {
    if (user?.is_admin) return '/admin/users';
    if (!granularPermissions?.sections?.length) return null;
    
    for (const sectionPerm of granularPermissions.sections) {
      if (sectionPerm.can_create || sectionPerm.can_delete) {
        const section = menuStructure.find(s => s.id === sectionPerm.section_id);
        const route = section ? sectionToRoute[section.name] : null;
        if (route) return route;
      }
      const section = menuStructure.find(s => s.id === sectionPerm.section_id);
      const validTabIds = new Set((section?.tabs || []).map(t => t.id));
      const hasTabPermission = sectionPerm.tabs?.some(
        tab => validTabIds.has(tab.tab_id) && (tab.can_read || tab.can_edit)
      );
      if (hasTabPermission && section) {
        const route = sectionToRoute[section.name];
        if (route) return route;
      }
    }
    return null;
  };

  const handleAdminClick = () => {
    // Session admin déjà déverrouillée pour CET utilisateur dans cet onglet
    const sessionKey = `admin_unlocked_${user?.id}`;
    if (sessionStorage.getItem(sessionKey) === 'true') {
      const route = getFirstAllowedRoute();
      if (route) navigate(route);
      setIsMobileOpen(false);
      return;
    }
    // Demander le PIN (ou sa création si non configuré)
    setShowPinModal(true);
  };

  const [showPinModal, setShowPinModal] = useState(false);

  const isAdminPage = location.pathname.startsWith('/admin');

  const sidebarInnerProps = {
    isCollapsed,
    setIsCollapsed,
    user,
    token,
    updateUser,
    onLogout,
    API,
    favoriteApps,
    onOpenApp,
    onToggleFavorite,
    onReorderFavorites,
    setIsMobileOpen,
    hasAdminAccess,
    isAdminPage,
    handleAdminClick,
    setCurrentSecteur,
  };

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={() => setIsMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg"
      >
        <Menu size={24} className="text-gray-700 dark:text-gray-300" />
      </button>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar Desktop */}
      <aside 
        className={`hidden lg:flex flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 h-screen sticky top-0 z-40 transition-all duration-300 ${
          isCollapsed ? 'w-20' : 'w-64'
        }`}
      >
        <SidebarInner {...sidebarInnerProps} />
      </aside>

      {/* Sidebar Mobile */}
      <aside 
        className={`lg:hidden fixed inset-y-0 left-0 z-50 flex flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 w-72 transform transition-transform duration-300 ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Close button mobile */}
        <button
          onClick={() => setIsMobileOpen(false)}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          <X size={20} className="text-gray-500" />
        </button>
        <SidebarInner {...sidebarInnerProps} />
      </aside>

      {/* Modal PIN double authentification administration */}
      <AdminPinModal
        open={showPinModal}
        onClose={() => setShowPinModal(false)}
        onSuccess={() => {
          setShowPinModal(false);
          sessionStorage.setItem(`admin_unlocked_${user?.id}`, 'true');
          const route = getFirstAllowedRoute();
          if (route) navigate(route);
          setIsMobileOpen(false);
        }}
      />
    </>
  );
};

export default Sidebar;
